import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";

import { Banner, Button, Checkbox, PasswordField, TextField, focusFirstInvalid } from "@ui/react";
import { applyPersistence, auth, rememberMe } from "../../lib/firebase";
import { firebaseAuthMessage } from "./firebaseErrors";
import { OnboardingLayout } from "./OnboardingLayout";

export function LoginScreen() {
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Contractors sign in from site offices and shared laptops as often as from
  // their own phone, so an unset preference means "forget me when the tab dies".
  const [remember, setRemember] = useState(rememberMe);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: { email?: string; password?: string } = {};
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!email.includes("@")) next.email = "Enter a valid email address.";
    if (!password) next.password = "Enter your password.";
    setFieldErrors(next);
    if (Object.keys(next).length) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      // Persistence is bound to the session at sign-in time — setting it after
      // signIn resolves would leave this session on the previous setting.
      await applyPersistence(remember);
      await signInWithEmailAndPassword(auth(), email.trim(), password);
      // No navigate() here: the router's gate reads the auth state and sends
      // the contractor to whichever onboarding step they still owe.
    } catch (error) {
      setFormError(firebaseAuthMessage(error, "We couldn't sign you in. Please try again."));
      setSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      eyebrow="Contractor portal"
      title="Sign in to Shoku."
      lead="Claim jobs, submit quotes, get paid on time."
      footer={
        <p className="muted">
          New to Shoku?{" "}
          <Link className="btn btn-tertiary" to="/register">
            Create an account
          </Link>
        </p>
      }
    >
      <form ref={formRef} className="form" onSubmit={onSubmit} noValidate>
        {formError ? <Banner tone="danger" title="Sign in failed">{formError}</Banner> : null}

        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@somewhere.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email ?? null}
          autoFocus
          required
        />

        <PasswordField
          label="Password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password ?? null}
          required
        />

        <Checkbox
          label="Keep me signed in on this device"
          checked={remember}
          onChange={(event) => setRemember(event.target.checked)}
        />

        <Button type="submit" size="lg" loading={submitting}>
          Sign in
        </Button>

        <Link className="btn btn-tertiary" to="/forgot-password">
          Forgot your password?
        </Link>
      </form>
    </OnboardingLayout>
  );
}
